import { useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import type { Geofence } from "@/lib/traccar-api";
import { MapStylePicker } from "@/components/map/MapStylePicker";
import { useMapInit } from "@/hooks/useMapInit";

interface GeofenceMapProps {
  geofences: Geofence[];
  selectedGeofence: Geofence | null;
  onGeofenceSelect?: (geofence: Geofence | null) => void;
  isDrawing?: boolean;
  drawType?: "circle" | "polygon";
  drawCenter?: [number, number] | null;
  drawRadius?: number;
  drawPolygon?: [number, number][];
  drawColor?: string;
  onMapClick?: (lngLat: [number, number]) => void;
}

// Helper to create circle coordinates
const createCircleCoordinates = (center: [number, number], radiusMeters: number, points = 64): [number, number][] => {
  const coords: [number, number][] = [];
  const earthRadius = 6371000; // meters
  const lat = center[1] * (Math.PI / 180);
  const lng = center[0] * (Math.PI / 180);
  const d = radiusMeters / earthRadius;

  for (let i = 0; i <= points; i++) {
    const bearing = (i * 360) / points * (Math.PI / 180);
    const latRadians = Math.asin(
      Math.sin(lat) * Math.cos(d) + Math.cos(lat) * Math.sin(d) * Math.cos(bearing)
    );
    const lngRadians = lng + Math.atan2(
      Math.sin(bearing) * Math.sin(d) * Math.cos(lat),
      Math.cos(d) - Math.sin(lat) * Math.sin(latRadians)
    );
    coords.push([lngRadians * (180 / Math.PI), latRadians * (180 / Math.PI)]);
  }
  return coords;
};

// Parse WKT to GeoJSON
const parseWKTToGeoJSON = (area: string): GeoJSON.Feature | null => {
  if (area.startsWith("CIRCLE")) {
    const match = area.match(/CIRCLE\s*\(\s*([-\d.]+)\s+([-\d.]+)\s*,\s*([-\d.]+)\s*\)/i);
    if (match) {
      const lat = parseFloat(match[1]);
      const lng = parseFloat(match[2]);
      const radius = parseFloat(match[3]);
      const coords = createCircleCoordinates([lng, lat], radius);
      return {
        type: "Feature",
        properties: {},
        geometry: { type: "Polygon", coordinates: [coords] }
      };
    }
  } else if (area.startsWith("POLYGON")) {
    const match = area.match(/POLYGON\s*\(\((.*)\)\)/i);
    if (match) {
      const coords = match[1].split(",").map((pair) => {
        const [lat, lng] = pair.trim().split(/\s+/).map(parseFloat);
        return [lng, lat] as [number, number];
      });
      return {
        type: "Feature",
        properties: {},
        geometry: { type: "Polygon", coordinates: [coords] }
      };
    }
  }
  return null;
};

export const GeofenceMap = ({
  geofences,
  selectedGeofence,
  onGeofenceSelect,
  isDrawing = false,
  drawType = "circle",
  drawCenter,
  drawRadius = 500,
  drawPolygon = [],
  drawColor = "#3b82f6",
  onMapClick,
}: GeofenceMapProps) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const [layersReady, setLayersReady] = useState(false);

  const { map, currentStyle, handleStyleChange } = useMapInit(mapContainer, {
    onBeforeStyleChange: () => setLayersReady(false),
  });

  useEffect(() => {
    if (!map.current || !mapContainer.current) return;

    const resizeObserver = new ResizeObserver(() => map.current?.resize());
    resizeObserver.observe(mapContainer.current);

    map.current.on('styledata', () => {
      if (!map.current || map.current.getSource('geofences')) return;
      addGeofenceLayers();
      setLayersReady(true);
    });

    if (isDrawing && onMapClick) {
      map.current.on('click', (e) => {
        onMapClick([e.lngLat.lng, e.lngLat.lat]);
      });
      map.current.getCanvas().style.cursor = 'crosshair';
    }

    return () => resizeObserver.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addGeofenceLayers = () => {
    if (!map.current) return;

    // Geofences source
    if (!map.current.getSource('geofences')) {
      map.current.addSource('geofences', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] }
      });
    }

    // Selected geofence source
    if (!map.current.getSource('selected-geofence')) {
      map.current.addSource('selected-geofence', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] }
      });
    }

    // Drawing source
    if (!map.current.getSource('drawing')) {
      map.current.addSource('drawing', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] }
      });
    }

    // Geofence fill layer (data-driven color per feature)
    if (!map.current.getLayer('geofences-fill')) {
      map.current.addLayer({
        id: 'geofences-fill',
        type: 'fill',
        source: 'geofences',
        paint: {
          'fill-color': ['coalesce', ['get', 'color'], '#3b82f6'],
          'fill-opacity': 0.15
        }
      });
    }

    // Geofence outline layer
    if (!map.current.getLayer('geofences-outline')) {
      map.current.addLayer({
        id: 'geofences-outline',
        type: 'line',
        source: 'geofences',
        paint: {
          'line-color': ['coalesce', ['get', 'color'], '#3b82f6'],
          'line-width': 2
        }
      });
    }

    // Selected geofence fill
    if (!map.current.getLayer('selected-geofence-fill')) {
      map.current.addLayer({
        id: 'selected-geofence-fill',
        type: 'fill',
        source: 'selected-geofence',
        paint: {
          'fill-color': ['coalesce', ['get', 'color'], '#3b82f6'],
          'fill-opacity': 0.35
        }
      });
    }

    // Selected geofence outline
    if (!map.current.getLayer('selected-geofence-outline')) {
      map.current.addLayer({
        id: 'selected-geofence-outline',
        type: 'line',
        source: 'selected-geofence',
        paint: {
          'line-color': ['coalesce', ['get', 'color'], '#3b82f6'],
          'line-width': 3
        }
      });
    }

    // Drawing fill
    if (!map.current.getLayer('drawing-fill')) {
      map.current.addLayer({
        id: 'drawing-fill',
        type: 'fill',
        source: 'drawing',
        paint: {
          'fill-color': ['coalesce', ['get', 'color'], '#3b82f6'],
          'fill-opacity': 0.2
        }
      });
    }

    // Drawing outline
    if (!map.current.getLayer('drawing-outline')) {
      map.current.addLayer({
        id: 'drawing-outline',
        type: 'line',
        source: 'drawing',
        paint: {
          'line-color': ['coalesce', ['get', 'color'], '#3b82f6'],
          'line-width': 2,
          'line-dasharray': [2, 2]
        }
      });
    }

    // Click handler for geofences
    if (!isDrawing) {
      map.current.on('click', 'geofences-fill', (e) => {
        if (e.features && e.features[0] && onGeofenceSelect) {
          const id = e.features[0].properties?.id;
          const geofence = geofences.find(g => g.id === id);
          if (geofence) onGeofenceSelect(geofence);
        }
      });

      map.current.on('mouseenter', 'geofences-fill', () => {
        if (map.current) map.current.getCanvas().style.cursor = 'pointer';
      });

      map.current.on('mouseleave', 'geofences-fill', () => {
        if (map.current) map.current.getCanvas().style.cursor = '';
      });
    }
  };

  // Update geofences on map
  useEffect(() => {
    if (!map.current || !layersReady) return;

    const source = map.current.getSource('geofences') as maplibregl.GeoJSONSource;
    if (!source) return;

    const features: GeoJSON.Feature[] = geofences
      .filter(g => g.id !== selectedGeofence?.id)
      .map(g => {
        const feature = parseWKTToGeoJSON(g.area);
        if (feature) {
          const color = typeof g.attributes.color === 'string' ? g.attributes.color : '#3b82f6';
          feature.properties = { id: g.id, name: g.name, color };
        }
        return feature;
      })
      .filter((f): f is GeoJSON.Feature => f !== null);

    source.setData({ type: 'FeatureCollection', features });
  }, [geofences, selectedGeofence, layersReady]);

  // Update selected geofence
  useEffect(() => {
    if (!map.current || !layersReady) return;

    const source = map.current.getSource('selected-geofence') as maplibregl.GeoJSONSource;
    if (!source) return;

    if (selectedGeofence) {
      const feature = parseWKTToGeoJSON(selectedGeofence.area);
      if (feature) {
        const color = typeof selectedGeofence.attributes.color === 'string' ? selectedGeofence.attributes.color : '#3b82f6';
        feature.properties = { color };
        source.setData({ type: 'FeatureCollection', features: [feature] });
        
        // Fit bounds to selected geofence
        const coords = (feature.geometry as GeoJSON.Polygon).coordinates[0];
        const bounds = coords.reduce(
          (b, c) => b.extend(c as [number, number]),
          new maplibregl.LngLatBounds(coords[0] as [number, number], coords[0] as [number, number])
        );
        map.current.fitBounds(bounds, { padding: 100, maxZoom: 15 });
      }
    } else {
      source.setData({ type: 'FeatureCollection', features: [] });
    }
  }, [selectedGeofence, layersReady]);

  // Update drawing preview
  useEffect(() => {
    if (!map.current || !layersReady || !isDrawing) return;

    const source = map.current.getSource('drawing') as maplibregl.GeoJSONSource;
    if (!source) return;

    const features: GeoJSON.Feature[] = [];

    if (drawType === "circle" && drawCenter) {
      const coords = createCircleCoordinates(drawCenter, drawRadius);
      features.push({
        type: "Feature",
        properties: { color: drawColor },
        geometry: { type: "Polygon", coordinates: [coords] }
      });

      // Pan to center
      map.current.flyTo({ center: drawCenter, zoom: 14 });
    } else if (drawType === "polygon" && drawPolygon.length >= 2) {
      // Show polygon preview (close it for display)
      const coords = [...drawPolygon, drawPolygon[0]];
      features.push({
        type: "Feature",
        properties: { color: drawColor },
        geometry: { type: "Polygon", coordinates: [coords] }
      });
      // Fit bounds when loading an existing polygon (more than 3 points = editing)
      if (drawPolygon.length >= 3) {
        const bounds = drawPolygon.reduce(
          (b, c) => b.extend(c),
          new maplibregl.LngLatBounds(drawPolygon[0], drawPolygon[0])
        );
        map.current.fitBounds(bounds, { padding: 60, maxZoom: 15 });
      }
    }

    source.setData({ type: 'FeatureCollection', features });
  }, [drawCenter, drawRadius, drawPolygon, drawType, drawColor, isDrawing, layersReady]);

  return (
    <div className="relative w-full h-full">
      <div ref={mapContainer} className="w-full h-full" />
      
      {/* Layer Switcher */}
      <div className="absolute top-3 left-3 z-10">
        <MapStylePicker currentStyle={currentStyle} onStyleChange={handleStyleChange} />
      </div>

      {/* Drawing instructions */}
      {isDrawing && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 bg-background/95 backdrop-blur-sm rounded-lg px-4 py-2 shadow-lg border">
          <p className="text-sm text-muted-foreground">
            {drawType === "circle" 
              ? "Click on the map to set the center point"
              : `Click to add points (${drawPolygon.length} added)`}
          </p>
        </div>
      )}
    </div>
  );
};

export default GeofenceMap;
